/**
 * Surfaces 3-6 — one component, four headings.
 *
 * Segments, audiences, populations and surveys are the same shape of thing (a row in `resource_grants`
 * with the same revoke endpoint) differing in what they are keyed by and what a wrong one costs. One
 * component with the resource type as a parameter, rather than four near-identical ones: four copies of
 * a revoke button is four places for the confirmation to be got wrong, and the confirmation is the whole
 * Heavy surface here.
 *
 * Each type appears under EXACTLY ONE heading. Segments are labelled "Healthcare chat" because that is
 * what a segment grant actually controls — `resource_type='segment'` feeds one document field,
 * `chat_segments`, and has no second consumer. Showing the same row under both "segments" and
 * "healthcare chat" would let an operator add it once, find it twice, and reasonably conclude access had
 * leaked somewhere.
 */
import { useState } from 'react';

import { ConfirmInline } from './ConfirmInline';
import { ResourcePicker, type PickerOption } from './ResourcePicker';
import { Row } from './Row';
import type { Grant } from '../types';

interface GrantsSurfaceProps {
  title: string;
  why: string;
  grants: Grant[];
  /** Options for the picker, already shaped by the dashboard, which owns the catalogue. */
  options: PickerOption[];
  /** "Add segments…" — the noun matters, because the tests and the operator both read the verb. */
  addLabel: string;
  submitLabel: string;
  /** Populations submit their names; everything else submits ids. */
  keyed: 'id' | 'key';
  onGrant: (values: string[]) => Promise<string | null>;
  onRevoke: (grant: Grant) => Promise<string | null>;
  busy: boolean;
  emptyHint?: string;
}

export function GrantsSurface({
  title,
  why,
  grants,
  options,
  addLabel,
  submitLabel,
  keyed,
  onGrant,
  onRevoke,
  busy,
  emptyHint,
}: GrantsSurfaceProps) {
  const [pendingRevoke, setPendingRevoke] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="border-t border-gray-100 px-4 py-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mb-2 mt-0.5 text-xs text-gray-500">{why}</p>

      {grants.length === 0 ? (
        <p className="py-1 text-xs text-gray-500">{emptyHint || 'Nothing granted yet.'}</p>
      ) : (
        grants.map((g) => (
          <Row
            key={g.id}
            source={g.source}
            createdByEmail={g.created_by_email}
            note={g.reachable === false ? <Unreachable /> : undefined}
            actions={
              // A seed-owned grant is not revocable here: the seed would put it straight back on the
              // next run, so the button would appear to work and silently not. The backend answers 409;
              // saying WHY beats letting them find out.
              g.source === 'seed' ? (
                <span
                  className="text-xs text-gray-400"
                  title="Remove it from configuration instead"
                >
                  configured
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setPendingRevoke(g.id)}
                  className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
                >
                  Revoke
                </button>
              )
            }
            below={
              pendingRevoke === g.id ? (
                <ConfirmInline
                  action="Revoke"
                  busy={busy}
                  consequence={
                    <>
                      Revoke <b>{describe(g)}</b>? They lose it immediately.
                    </>
                  }
                  onConfirm={async () => {
                    setError(await onRevoke(g));
                    setPendingRevoke(null);
                  }}
                  onCancel={() => setPendingRevoke(null)}
                />
              ) : null
            }
          >
            {describe(g)}
          </Row>
        ))
      )}

      <button
        type="button"
        onClick={() => setPickerOpen((open) => !open)}
        className="mt-2 rounded border border-teal-700 px-2.5 py-1 text-xs font-medium text-teal-800 hover:bg-teal-50"
      >
        {addLabel}
      </button>

      {pickerOpen && (
        <ResourcePicker
          options={options}
          submitLabel={submitLabel}
          busy={busy}
          emptyHint={
            options.length === 0
              ? 'Nothing to offer — the catalogue is empty until something exists to grant.'
              : undefined
          }
          onCancel={() => setPickerOpen(false)}
          onSubmit={async (values) => {
            const failure = await onGrant(values);
            setError(failure);
            if (!failure) setPickerOpen(false);
          }}
        />
      )}

      {error && (
        <p role="alert" className="mt-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {error}
        </p>
      )}
      {/* Documents the branch above for a reader; `keyed` is consumed by the dashboard's submit. */}
      <span hidden data-keyed={keyed} />
    </section>
  );
}

/**
 * Both the id and the name, always, in that order — the id is what the grant stores and the name is
 * what a human recognises. Showing one without the other is how 753 stayed wrong.
 */
function describe(g: Grant): string {
  const name = g.label || g.resource_key || '';
  if (g.resource_id === null) return name || '(unnamed)';
  return name ? `${g.resource_id} · ${name}` : String(g.resource_id);
}

function Unreachable() {
  return (
    <span
      className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900"
      title="No segment_chat_config row, so no chat surface can open it"
    >
      unreachable
    </span>
  );
}

export default GrantsSurface;
